import * as React from "react";

import { AccessReportsColumn, AccessReportsWizardGroupPropertyIdEnum, AccessReportsWizardOption, ColumnDefaults, SelectedColumnsByFilter } from "@app/access-reports/shared";
import { cloneDeep, isEqual, uniqWith } from "lodash";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";

interface EditPrintColumnsProps {
  accessFilters: AccessReportsWizardOption[];
  columnDefaults: ColumnDefaults;
  columns: AccessReportsColumn[];
  disableButtons: boolean;
  groupPropertyId: AccessReportsWizardGroupPropertyIdEnum;
  hideSidePanel: () => void;
  onSaveColumnsClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults) => void;
}

interface EditPrintColumnsState {
  accessFilterDropdownVisible: boolean;
  columnClearConfirmationVisible: boolean;
  columnDefaults: ColumnDefaults;
  lastAddedFilterId?: number;
  selectAll: boolean;
  selectedAccessFilters: AccessReportsWizardOption[];
}

class EditPrintColumns extends React.Component<EditPrintColumnsProps, EditPrintColumnsState> {
  private accessFiltersButtonRef = React.createRef<HTMLButtonElement>();

  constructor(props: EditPrintColumnsProps) {
    super(props);

    this.state = {
      accessFilterDropdownVisible: false,
      columnClearConfirmationVisible: false,
      columnDefaults: null,
      lastAddedFilterId: null,
      selectAll: false,
      selectedAccessFilters: [],
    };
  }

  public componentDidMount() {
    this.extractColumns();
  }

  public componentDidUpdate(prevProps: EditPrintColumnsProps) {
    if (!isEqual(prevProps.columnDefaults, this.props.columnDefaults)) {
      this.extractColumns();
    }

    if (this.state.accessFilterDropdownVisible) {
      document.body.addEventListener("click", this.onMouseClick);
    } else {
      document.body.removeEventListener("click", this.onMouseClick);
    }
  }

  public componentWillUnmount() {
    document.body.removeEventListener("click", this.onMouseClick);
  }

  public render() {
    return (
      <React.Fragment>
        <Modal
          isVisible={this.state.columnClearConfirmationVisible}
          content={{
            body: "Selecting multiple access filters with different selected columns will clear all column selections for those filters. Do you wish to continue?",
            cancelButtonText: "Cancel",
            confirmButtonAction: () => this.hideColumnClearConfirmation(true),
            confirmButtonText: "Continue",
            confirmButtonType: "primary",
            title: "Warning",
          }}
          closeModal={() => this.hideColumnClearConfirmation(false)}
          triggerElement={this.accessFiltersButtonRef}
        />

        <div className="row">
          <div className="col-12">
            <QESAlert classNames="mb-0" type="info" persist={true}>
              There can only be a limited number of columns on a printed report.
            </QESAlert>
          </div>
        </div>

        {(this.props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage || this.props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) ?
          this.buildColumnsCheckboxes() :
          this.props.accessFilters.length > 0 && (
            <React.Fragment>
              <div className="row">
                <div className="col-12">
                  <div className="form-group mt-3 mb-0">
                    <legend className="mb-0">
                      Access Filter
                    </legend>

                    <div className="position-relative checkbox-dropdown-container">
                      <QESButton
                        qesTestId="access-reports-admin__print-columns--access-filters"
                        qesType="secondary"
                        className="d-flex h-100 w-100 align-items-center justify-content-between access-reports-groups-btn"
                        onClick={() => this.setAccessFilterDropdownVisible()}
                        qesRef={this.accessFiltersButtonRef}
                      >
                        <span>
                          {(this.state.selectedAccessFilters.length > 0) ?
                            this.state.selectedAccessFilters.map(x => !!x.displayName ? x.displayName : x.name).join(", ") :
                            "\u00A0"
                          }
                        </span>
                        <i className="fa fa-caret-down" aria-hidden="true" />
                      </QESButton>
                      {this.state.accessFilterDropdownVisible && (
                        <div className="checkbox-dropdown mt-0 w-100">
                          {this.props.accessFilters.map((accessFilter, accessFilterIndex) => (
                            <QESCheckbox
                              containerClassName="checkbox-dropdown__label cursor-pointer"
                              label={!!accessFilter.displayName ? accessFilter.displayName : accessFilter.name}
                              checked={this.state.selectedAccessFilters.map(x => x.id).includes(accessFilter.id)}
                              className="checkbox-dropdown__checkbox"
                              onChange={() => this.onAccessFilterChange(accessFilter)}
                              key={accessFilterIndex}
                              testId={`access-reports-admin__print-columns--access-filters-${accessFilter.id}`}
                            />
                          ))}

                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {this.state.selectedAccessFilters.length > 0 &&
                this.buildColumnsCheckboxes()
              }
            </React.Fragment>
          )}

        <FlyoutFooter
          submitButton={
            <QESButton
              className="cursor-pointer ml-3"
              disabled={this.props.disableButtons}
              onClick={() => this.props.onSaveColumnsClick(this.props.groupPropertyId, this.state.columnDefaults)}
              qesTestId="access_reports_admin__print-columns--save"
              qesType="primary"
            >
              Save
            </QESButton>
          }
        />
      </React.Fragment>
    );
  }

  private buildColumnsCheckboxes = (): React.ReactNode => {
    if ((this.state.selectedAccessFilters.length > 0 || (this.props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage || AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages)) && !!this.state.columnDefaults) {
      const columnTypes: string[] = [];
      for (const column of this.props.columns) {
        if (!columnTypes.includes(column.columnType)) {
          columnTypes.push(column.columnType);
        }
      }

      if (columnTypes.length) {
        return (
          <React.Fragment>
            <div className="row">
              <div className="col-12">
                <QESCheckbox
                  checked={this.state.selectAll}
                  containerClassName="mt-3 mb-0"
                  disabled={this.props.disableButtons}
                  label="Select All"
                  testId="access-reports-admin__print-columns--select-all-checkbox"
                  onChange={() => this.onColumnToggle()}
                />
              </div>
            </div>

            {columnTypes.map((columnType, columnTypeIndex) => {
              const columns: AccessReportsColumn[] = this.props.columns.filter(x => x.columnType === columnType);

              return (
                <React.Fragment key={columnTypeIndex}>
                  <div className="row">
                    <div className="col-12">
                      <legend className="mt-3 mb-0">
                        {columnType}
                      </legend>
                    </div>
                  </div>

                  {columns.map((column, columnIndex) => (
                    <div className="row" key={`${columnTypeIndex}-${columnIndex}`}>
                      <div className="col-12">
                        <QESCheckbox
                          checked={this.getColumnValue(column.id)}
                          disabled={this.props.disableButtons}
                          label={column.displayName ? column.displayName : column.name}
                          testId={`access-reports-admin__print-columns--checkbox-${columnTypeIndex}-${columnIndex}`}
                          onChange={() => this.onColumnToggle(column.id)}
                        />
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      }
    }

    return (
      <div className="row">
        <div className="col-12">
          <p>
            <i className="text-danger fa fa-fw fa-exclamation-circle" />
            <span className="text-muted">
              Error
            </span>
          </p>
        </div>
      </div>
    );
  }

  private extractColumns = (): void => {
    if (!!this.props.columnDefaults) {
      const columnDefaults: ColumnDefaults = cloneDeep(this.props.columnDefaults);
      let selectedAccessFilters: AccessReportsWizardOption[] = this.state.selectedAccessFilters;
      const selectedColumns: SelectedColumnsByFilter = columnDefaults.selectedColumns.find(x => x.filterId === 0);

      if (!!this.props.accessFilters?.length &&
        (this.props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && this.props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) 
      ) {
        // count pages do not have an access filter
        selectedAccessFilters = [this.props.accessFilters[0]];
      }

      this.setState({
        columnDefaults,
        selectAll: selectedColumns?.columns.length === this.props.columns.length,
        selectedAccessFilters,
      });
    }
  }

  private getColumnValue = (columnId: number): boolean => {
    let filter = 0;

    if (this.props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && this.props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
      filter = this.state.selectedAccessFilters[0].id;
    }

    const selectedColumnsByFilter: SelectedColumnsByFilter = this.state.columnDefaults.selectedColumns.find(x => x.filterId === filter);

    if (selectedColumnsByFilter?.columns.includes(columnId)) {
      return true;
    }

    return false;
  }

  private getPageType = (): string => {
    switch (this.props.groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
        return "Access Detail";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        return "Access Summary";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
        return "Employee Count";
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        return "Access Listing";
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        return "Access Radius Search";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
        return "Provider Count";
      default:
        console.error(`Invalid Group Property Id: ${this.props.groupPropertyId}`);
        break;
    }
  }

  private hideColumnClearConfirmation = (columnClearConfirmed: boolean): void => {
    if (columnClearConfirmed) {
      // clear columns for all currently-selected filters
      const columnDefaults: ColumnDefaults = cloneDeep(this.state.columnDefaults);
      const filteredSelectedColumns = columnDefaults.selectedColumns.filter(x => this.state.selectedAccessFilters.map(x => x.id).includes(x.filterId));

      for (const selectedColumns of filteredSelectedColumns) {
        selectedColumns.columns = [];
      }

      this.setState({
        columnClearConfirmationVisible: false,
        columnDefaults,
        selectAll: false,
      });
    } else {
      // if the column clear is not confirmed, deselect the last filter that was selected
      const selectedAccessFilters: AccessReportsWizardOption[] = cloneDeep(this.state.selectedAccessFilters);

      this.setState({
        columnClearConfirmationVisible: false,
        selectedAccessFilters: selectedAccessFilters.filter(x => x.id !== this.state.lastAddedFilterId),
      });
    }
  }

  private onAccessFilterChange = (accessFilter: AccessReportsWizardOption): void => {
    let columnClearConfirmationVisible = false;
    let selectedAccessFilters: AccessReportsWizardOption[] = cloneDeep(this.state.selectedAccessFilters);

    if (selectedAccessFilters.map(x => x.id).includes(accessFilter.id)) {
      selectedAccessFilters = selectedAccessFilters.filter(x => x.id !== accessFilter.id);
    } else {
      selectedAccessFilters.push(accessFilter);
    }

    // determine whether the select all checkbox should be checked
    let filterId = 0;

    if (this.props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && this.props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
      filterId = selectedAccessFilters[0]?.id;
    }

    const selectedColumns: SelectedColumnsByFilter = this.state.columnDefaults.selectedColumns.find(x => x.filterId === filterId);

    // compare each selected filter's selected columns:
    const filteredSelectedColumns: SelectedColumnsByFilter[] = this.state.columnDefaults.selectedColumns.filter(x => selectedAccessFilters.map(x => x.id).includes(x.filterId));
    // the order of the columns array does not matter; sort to prevent "differences"
    const unique = uniqWith(filteredSelectedColumns.map(x => x.columns.sort((n1, n2) => n1 - n2)), isEqual);

    // if there is a difference, then prompt the column clear confirmation modal
    if (unique.length > 1) {
      columnClearConfirmationVisible = true;
    }

    this.setState({
      columnClearConfirmationVisible,
      lastAddedFilterId: accessFilter.id,
      selectAll: selectedColumns?.columns.length === this.props.columns.length,
      selectedAccessFilters,
    });
  }

  private onColumnToggle = (columnId?: number): void => {
    const columnDefaults: ColumnDefaults = cloneDeep(this.state.columnDefaults);
    let selectAll = this.state.selectAll;

    const toggleColumn = (filterId: number): void => {
      let selectedColumns: SelectedColumnsByFilter = columnDefaults.selectedColumns.find(x => x.filterId === filterId);

      if (!selectedColumns) {
        selectedColumns = {
          columns: [],
          filterId: filterId,
        }

        columnDefaults.selectedColumns.push(selectedColumns);
      }

      if (!!columnId) {
        // individual checkbox toggle
        if (selectedColumns.columns.includes(columnId)) {
          selectedColumns.columns = selectedColumns.columns.filter(x => x !== columnId);
        } else {
          selectedColumns.columns.push(columnId);
        }
      } else {
        // select all toggle
        if (this.state.selectAll) {
          selectedColumns.columns = [];
        } else {
          selectedColumns.columns = this.props.columns.map(x => x.id);
        }
      }

      if (selectedColumns.columns.length === 0) {
        // removing the node if it doesn't contain columns in order to preserve template space
        columnDefaults.selectedColumns.filter(x => x.filterId === filterId);
      }

      selectAll = selectedColumns.columns.length === this.props.columns.length;
    }

    if (this.props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage || this.props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
      toggleColumn(0);
    } else {
      for (const filter of this.state.selectedAccessFilters) {
        toggleColumn(filter.id);
      }
    }

    this.setState({
      columnDefaults,
      selectAll,
    });
  }

  private onMouseClick = (e: MouseEvent): void => {
    const target: HTMLElement = e.target as HTMLElement;

    if (target.closest(".checkbox-dropdown-container") === null && this.state.accessFilterDropdownVisible) {
      this.setState({
        accessFilterDropdownVisible: false,
      });
    }
  }

  private setAccessFilterDropdownVisible = (): void => {
    this.setState({
      accessFilterDropdownVisible: true,
    });
  }
}

export { EditPrintColumns };
